import {
  Component,
  createEffect,
  createSignal,
  Match,
  on,
  Switch,
} from 'solid-js';
import { ModelDetails } from './ModelDetails';
import { ModelVersions } from './ModelVersions';
import { PositionSettings } from '../settings/position/PositionSettings';
import {
  BtnClasses,
  ConfirmPopup,
  IconButton,
  PanelLayout,
} from '@tcweb/connect-ui';
import { t } from '@trimble/i18n';
import { UseQueryResult } from '@tanstack/solid-query';
import {
  FileDetails,
  ListallCommentsResponse,
  ListTagsinaprojectResponse,
  TcModelAlignment,
} from '@data-access/connect/tc-tcps';
import { FormattingSettings } from '@trimble/types';

export enum ModelDetailsView {
  Details = 'details',
  Versions = 'versions',
  Position = 'position',
}

export interface ModelDetailsContainerProps {
  projectExplorerUrl: string;
  modelName: string;
  fileDetailsQuery?: UseQueryResult<FileDetails, Error>;
  commentsQuery?: UseQueryResult<ListallCommentsResponse, Error>;
  tagsQuery?: UseQueryResult<ListTagsinaprojectResponse, Error>;
  fileVersionsQuery?: UseQueryResult<FileDetails[], Error>;
  positionAlignment?: TcModelAlignment;
  positionSaving?: boolean;
  formattingSettings?: FormattingSettings;
  getImageBlobUrl(thumbnailUrl: string): Promise<string>;
  getFileDownloadUrl(item: FileDetails): Promise<string>;
  onViewChange?(view: ModelDetailsView): void;
  onApplyAlignment?(alignment: TcModelAlignment): void;
  onRevertPosition?(): void;
  onSavePosition?(alignment: TcModelAlignment): Promise<boolean>;
  onClose(): void;
}

export const ModelDetailsContainer: Component<ModelDetailsContainerProps> = (
  props
) => {
  const [view, setView] = createSignal<ModelDetailsView>(
    ModelDetailsView.Details
  );
  const [positionHasChanges, setPositionHasChanges] = createSignal(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = createSignal(false);
  const [pendingPositionExit, setPendingPositionExit] = createSignal<
    'back' | 'close' | null
  >(null);

  const handleSavePosition = async (
    alignment: TcModelAlignment
  ): Promise<void> => {
    const saveSucceeded = (await props.onSavePosition?.(alignment)) ?? true;
    if (!saveSucceeded) {
      return;
    }

    setView(ModelDetailsView.Details);
  };

  const navigateFromPosition = (exit: 'back' | 'close'): void => {
    props.onRevertPosition?.();

    if (exit === 'back') {
      setView(ModelDetailsView.Details);
      return;
    }

    props.onClose();
  };

  const requestExitFromPosition = (exit: 'back' | 'close'): void => {
    if (positionHasChanges()) {
      setPendingPositionExit(exit);
      setShowDiscardConfirm(true);
      return;
    }

    navigateFromPosition(exit);
  };

  const handleDiscardCancel = (): void => {
    setShowDiscardConfirm(false);
    setPendingPositionExit(null);
  };

  const handleDiscardConfirm = (): void => {
    const exit = pendingPositionExit();
    setShowDiscardConfirm(false);
    setPendingPositionExit(null);

    if (exit) {
      navigateFromPosition(exit);
    }
  };

  createEffect(
    on(
      () => view(),
      (nextView) => {
        if (nextView !== ModelDetailsView.Position) {
          setPositionHasChanges(false);
          setShowDiscardConfirm(false);
          setPendingPositionExit(null);
        }
      }
    )
  );

  // if the model changes, reset the view to details
  createEffect(
    on(
      () => props.fileDetailsQuery,
      () => {
        setView(ModelDetailsView.Details);
      }
    )
  );

  // if the view changes, call the onViewChange callback
  createEffect(
    on(
      () => view(),
      (view) => {
        props.onViewChange?.(view);
      }
    )
  );

  return (
    <>
      <Switch>
        <Match when={view() === ModelDetailsView.Details}>
          <PanelLayout
            panelTitle={props.modelName}
            rightActions={
              <IconButton
                color={BtnClasses.TERTIARY}
                icon="tc-icon-content-close"
                size="icon-medium"
                data-cy="model-details-close"
                clicked={() => {
                  props.onClose();
                }}
              />
            }
            content={
              <ModelDetails
                projectExplorerUrl={props.projectExplorerUrl}
                fileDetailsQuery={props.fileDetailsQuery}
                commentsQuery={props.commentsQuery}
                tagsQuery={props.tagsQuery}
                onViewVersions={() => void setView(ModelDetailsView.Versions)}
                onViewPosition={() => void setView(ModelDetailsView.Position)}
              />
            }
          />
        </Match>

        <Match when={view() === ModelDetailsView.Versions}>
          <PanelLayout
            panelTitle={t('shared.ui.modelVersions.title')}
            leftActions={
              <IconButton
                color={BtnClasses.TERTIARY}
                icon="tc-icon-arrow-line-back"
                size="icon-medium"
                data-cy="model-versions-back"
                clicked={() => void setView(ModelDetailsView.Details)}
              />
            }
            rightActions={
              <IconButton
                color={BtnClasses.TERTIARY}
                icon="tc-icon-content-close"
                size="icon-medium"
                data-cy="model-versions-close"
                clicked={() => void props.onClose()}
              />
            }
            content={
              <ModelVersions
                fileVersionsQuery={props.fileVersionsQuery}
                getImageBlobUrl={(thumbnailUrl) =>
                  props.getImageBlobUrl(thumbnailUrl)
                }
                getFileDownloadUrl={(item) => props.getFileDownloadUrl(item)}
              />
            }
          />
        </Match>

        <Match when={view() === ModelDetailsView.Position}>
          <PanelLayout
            panelTitle={t('shared.ui.modelPosition.title')}
            leftActions={
              <IconButton
                color={BtnClasses.TERTIARY}
                icon="tc-icon-arrow-line-back"
                size="icon-medium"
                data-cy="model-position-back"
                clicked={() => requestExitFromPosition('back')}
              />
            }
            rightActions={
              <IconButton
                color={BtnClasses.TERTIARY}
                icon="tc-icon-content-close"
                size="icon-medium"
                data-cy="model-position-close"
                clicked={() => requestExitFromPosition('close')}
              />
            }
            content={
              props.positionAlignment &&
              props.formattingSettings && (
                <PositionSettings
                  alignment={props.positionAlignment}
                  formattingSettings={props.formattingSettings}
                  isSaving={props.positionSaving}
                  onApplyAlignment={props.onApplyAlignment}
                  onCancel={() => void navigateFromPosition('back')}
                  onHasUnsavedChanges={setPositionHasChanges}
                  onSave={handleSavePosition}
                />
              )
            }
          />
        </Match>
      </Switch>

      <ConfirmPopup
        show={showDiscardConfirm()}
        title={t('shared.ui.modelPosition.discardConfirmTitle')}
        description={t('shared.ui.modelPosition.discardConfirmDescription')}
        primaryBtnLabel={t('shared.ui.modelPosition.discardChanges')}
        primaryBtnClass={BtnClasses.DANGER}
        onClose={handleDiscardCancel}
        onAction={handleDiscardConfirm}
      />
    </>
  );
};
